import React from 'react';
import {
  Box,
  HintPlacement,
  Inscription,
  List,
  PaginationBar,
  SearchInput,
  SelectDropdown,
  SimpleHint,
} from '@common-fe/common-fe';

import { Option, OptionKey } from '@/common/types';

import { UnpaidInvoice } from '../../UnappliedFunds.types';

import UnpaidInvoiceItem from './UnpaidInvoiceItem';
import { HeaderCheckBox } from './UnpaidInvoices.styles';

interface Props {
  invoices: UnpaidInvoice[];
  total: number;
  isLoading: boolean;
  hasActiveFilters: boolean;
  page: number;
  perPage: number;
  onChangePage: (page: number) => void;
  onChangePerPage: (perPage: number) => void;
  searchString: string;
  onChangeSearch: (value: string) => void;
  typeOptions: Option[];
  types: OptionKey[];
  onChangeTypes: (types: OptionKey[]) => void;
  selectedIds: string[];
  disabledIds: string[];
  isAllSelected: boolean;
  isSelectAllDisabled: boolean;
  onToggleInvoice: (id: string, checked: boolean) => void;
  onToggleAll: (checked: boolean) => void;
}

const UnpaidInvoices: React.FC<Props> = ({
  invoices,
  total,
  isLoading,
  hasActiveFilters,
  page,
  perPage,
  onChangePage,
  onChangePerPage,
  searchString,
  onChangeSearch,
  typeOptions,
  types,
  onChangeTypes,
  selectedIds,
  disabledIds,
  isAllSelected,
  isSelectAllDisabled,
  onToggleInvoice,
  onToggleAll,
}) => {
  const isEmpty = !isLoading && total === 0;
  const hasNoUnpaidInvoices = isEmpty && !hasActiveFilters;
  const isSelectAllHintVisible = isSelectAllDisabled && !isLoading && invoices.length > 0;

  const selectAllCheckbox = (
    <HeaderCheckBox
      name="unpaidInvoicesAll"
      role="checkbox"
      aria-label="unpaidInvoicesAll"
      data-testid="unpaid-invoices_select-all"
      checked={isAllSelected}
      disabled={isLoading || invoices.length === 0 || isSelectAllDisabled}
      onChange={({ target }) => onToggleAll(target.checked)}
    />
  );

  return (
    <Box
      data-testid="unpaid-invoices"
      pad="spacing24"
      background="module"
      round="container1Round"
    >
      <Box direction="row" align="center" justify="between" margin={{ bottom: 'spacing24' }}>
        <Inscription weight="bold" color="textBody">
          Invoices: <Inscription data-testid="unpaid-invoices_total">{total}</Inscription>
        </Inscription>
        <Box direction="row" gap="spacing12" align="center">
          <Box width="260px">
            <SearchInput
              testId="unpaid-invoices_search"
              value={searchString}
              onChange={onChangeSearch}
              placeholder="Search invoice or amount"
              disabled={hasNoUnpaidInvoices}
            />
          </Box>
          <Box width="142px">
            <SelectDropdown
              id="unpaid_invoices_type"
              name="Invoice type"
              prefix="Type"
              testId="unpaid-invoices_type-filter"
              options={typeOptions}
              values={types}
              onChangeValues={onChangeTypes}
              disabled={hasNoUnpaidInvoices}
              allMode
              activeTitle
              ellipsisMode
              dropdownStyles={{ minWidth: '250px' }}
            />
          </Box>
        </Box>
      </Box>

      <List>
        {!isEmpty && (
          <List.Header gap="spacing24">
            <Box width="24px" align="start">
              {isSelectAllHintVisible ? (
                <SimpleHint
                  preferAlign={HintPlacement.TOP}
                  isFixed
                  usePortal
                  width="200px"
                  round="container1Round"
                  testId="unpaid-invoices_select-all-hint"
                  content={
                    <Inscription
                      data-testid="unpaid-invoices_select-all-hint_text"
                      size="small"
                      weight={500}
                      lineHeight="18px"
                      color="textOnColor"
                      textAlign="center"
                    >
                      Selecting all exceeds your available unapplied funds. Please select invoices manually.
                    </Inscription>
                  }
                >
                  {selectAllCheckbox}
                </SimpleHint>
              ) : (
                selectAllCheckbox
              )}
            </Box>
            <Box width="240px">
              <List.Header.Item>Invoice #</List.Header.Item>
            </Box>
            <Box width="160px">
              <List.Header.Item>Type</List.Header.Item>
            </Box>
            <Box width="110px">
              <List.Header.Item>Invoice date</List.Header.Item>
            </Box>
            <Box flex>
              <List.Header.Item textAlign="end">Amount</List.Header.Item>
            </Box>
          </List.Header>
        )}

        {isLoading && <List.Preloader testId="unpaid-invoices_preloader" count={3} />}

        {hasNoUnpaidInvoices && (
          <Box pad={{ vertical: 'spacing24' }} align="center">
            <Inscription weight="bold" data-testid="unpaid-invoices_empty-text">
              There are no unpaid invoices
            </Inscription>
          </Box>
        )}

        {isEmpty && hasActiveFilters && <List.Placeholder testId="unpaid-invoices_no-results" />}

        {!isLoading && invoices.map((invoice) => (
            <UnpaidInvoiceItem
              key={invoice.id}
              {...invoice}
              isSelected={selectedIds.includes(invoice.id)}
              isDisabled={disabledIds.includes(invoice.id)}
              onToggle={onToggleInvoice}
            />
          ))}
      </List>

      <PaginationBar
        page={page}
        total={total}
        pageSize={perPage}
        onChangePage={onChangePage}
        onChangePageSize={onChangePerPage}
      />
    </Box>
  );
};

export default UnpaidInvoices;
